import { DatePipe } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import writeXlsxFile, { type CellObject, type SheetData } from 'write-excel-file/browser';
import { saveAs } from 'file-saver';
import { ExcelRequestColumn } from 'src/app/models/ExcelRequestColumn';
import { PersonQuery } from 'src/app/models/FilesRequest';
import { KpisGroup } from 'src/app/models/KpisGroup';
import { UtilsService } from 'src/app/utils/utils.service';

@Injectable({
    providedIn: 'root'
})
export class ExcelService {
    private readonly translate = inject(TranslateService);
    private readonly utilsService = inject(UtilsService);
    private readonly datePipe = inject(DatePipe);

    async exportList(list: PersonQuery[], cols: ExcelRequestColumn[] = this.getDefaultCols()): Promise<void> {
        if (!list || !list.length || !cols || !cols.length) return;

        const headers = cols.map(c => this.translate.instant(c.i18n));
        const data: SheetData = [headers.map(value => this.cell(value, {
            fontWeight: 'bold',
            alignVertical: 'center',
            height: 26
        }))];

        const phoneColIndex = cols.findIndex(c => c.key === 'phoneNumber') + 1;

        for (const r of list) {
            const row = cols.map((c, index) => {
                const value = c.value ? c.value(r) : r[c.key];
                const cell = this.cell(value, {
                    alignVertical: 'center',
                    align: index + 1 === phoneColIndex ? 'left' : undefined,
                    height: 26,
                    wrap: true
                });

                if (index + 1 === phoneColIndex) {
                    const raw = (r.phoneNumber ?? '').toString().trim();

                    if (this.isExcelSafeNumberPhone(raw)) {
                        cell.value = +raw;
                        cell.format = '0';
                    }
                }
                return cell;
            });
            data.push(row);
        }

        const blob = await writeXlsxFile(data, {
            sheet: this.translate.instant('REQUESTS'),
            columns: cols.map(c => ({ width: c.width ?? 16 }))
        }).toBlob();
        this.save(blob, 'opendataweb_requests');
    }

    // #region KPIs Excel
    private readonly kpisScoreKey = 'KPI.KEY.score';

    private readonly kpisRatingCells: { label: string; minScore: number; argb: string }[] = [
        { label: 'VERY_LOW',    minScore: 0,   argb: 'FFE57373' },
        { label: 'LOW',         minScore: 262, argb: 'FFFFB74D' },
        { label: 'GOOD',        minScore: 345, argb: 'FFFFF176' },
        { label: 'VERY_GOOD',   minScore: 481, argb: 'FF81C784' },
        { label: 'EXCEPTIONAL', minScore: 510, argb: 'FF4CAF50' }
    ];

    generateKpisExcelBlob(kpiGroups: KpisGroup[]): Promise<Blob> {
        const data = this.buildKpisSheet(kpiGroups);
        return writeXlsxFile(data, {
            sheet: 'KPIs',
            columns: [{ width: 56 }, { width: 56 }, { width: 56 }]
        }).toBlob();
    }

    private buildKpisSheet(kpiGroups: KpisGroup[]): SheetData {
        const data: SheetData = [[
            this.cell(this.translate.instant('IRPF.HEADERS.CONCEPT'), { fontWeight: 'bold', backgroundColor: '#EFEFEF', align: 'center', alignVertical: 'center', wrap: true, height: 22 }),
            this.cell(this.translate.instant('ADMINISTRATION.FEASIBILITY.DESCRIPTION'), { fontWeight: 'bold', backgroundColor: '#EFEFEF', align: 'center', alignVertical: 'center', wrap: true, height: 22 }),
            this.cell(this.translate.instant('ADMINISTRATION.FEASIBILITY.VALUE'), { fontWeight: 'bold', backgroundColor: '#EFEFEF', align: 'center', alignVertical: 'center', wrap: true, height: 22 })
        ]];

        for (const group of kpiGroups) {
            data.push([this.cell(this.translate.instant(group.title).toUpperCase(), { columnSpan: 3, fontWeight: 'bold', backgroundColor: '#EFEFEF', align: 'left', alignVertical: 'center', wrap: true, height: 25 })]);

            for (const kpi of group.kpis) {
                const title = this.cell(this.translate.instant(kpi.key), { alignVertical: 'center', wrap: true, height: 56 });
                let description: CellObject = this.cell(undefined, { alignVertical: 'center', wrap: true, height: 56 });
                if (kpi.description) {
                    description = this.cell(this.translate.instant(kpi.description), { fontStyle: 'italic', textColor: '#757575', alignVertical: 'center', wrap: true, height: 56 });
                }
                let value: CellObject = this.cell(undefined, { align: 'center', alignVertical: 'center', wrap: true, height: 56 });
                if (kpi.key === this.kpisScoreKey) {
                    const rating = this.getKpiRatingForScore(Number(kpi.value));
                    value = this.cell(kpi.value, { backgroundColor: `#${rating.argb.slice(2)}`, fontWeight: 'bold', align: 'center', alignVertical: 'center', wrap: true, height: 56 });
                } else if (typeof kpi.value === 'number') {
                    value = this.cell(kpi.value, { format: '#,##0.00', align: 'center', alignVertical: 'center', wrap: true, height: 56 });
                } else if (typeof kpi.value === 'boolean') {
                    value = this.cell(kpi.value ? this.translate.instant('BOOLEAN.TRUE') : this.translate.instant('BOOLEAN.FALSE'), { align: 'center', alignVertical: 'center', wrap: true, height: 56 });
                } else if (kpi.value !== null && kpi.value !== undefined && kpi.value !== '') {
                    const translated = this.translate.instant(String(kpi.value));
                    value = this.cell(translated === String(kpi.value) ? String(kpi.value) : translated, { align: 'center', alignVertical: 'center', wrap: true, height: 56 });
                }
                data.push([title, description, value]);
            }
        }

        return data;
    }

    private getKpiRatingForScore(score: number): { label: string; argb: string } {
        let rating = this.kpisRatingCells[0];
        for (const cell of this.kpisRatingCells) {
            if (score >= cell.minScore) rating = cell;
        }
        return rating;
    }
    // #endregion

    // #region Private Methods
    private cell(value: any, style: Partial<CellObject> = {}): CellObject {
        return { value, ...style };
    }

    private save(data: Blob, fileName: string): void {
        saveAs(data, `${fileName}_export_${new Date().getTime()}_${this.translate.currentLang}.xlsx`);
    }

    private isExcelSafeNumberPhone(s: string): boolean {
	    return /^\d{9}$/.test(s) && !s.startsWith('0');
    }

    private getDefaultCols(): ExcelRequestColumn[] {
	    return [
	        {
	            key: 'queryId',
	            i18n: 'MY_REQUESTS.QUERY_ID',
	            width:36
	        },
	        {
	            key: 'queryType',
	            i18n: 'FILTER.REQUEST_TYPE',
	            value: (r) => r.queryType === 'PhysicalPerson' ? this.translate.instant('FILTER.QUERY_TYPES.PHYSICAL_PERSON') : r.queryType
	        },
	        {
	            key: 'personId',
	            i18n: 'MY_REQUESTS.ID',
	            width: 12
	        },
	        {
	            key: 'name',
	            i18n: 'FILTER.PERSON_ENTITY',
	            width: 34,
	            value: (r) => this.utilsService.getFullName(r)
	        },
	        {
	            key: 'email',
	            i18n: 'FILTER.EMAIL',
	            width: 22
	        },
	        {
	            key: 'phoneNumber',
	            i18n: 'FILTER.PHONE',
	            width: 12
	        },
	        {
	            key: 'createdDateTimeUTC',
	            i18n: 'MY_REQUESTS.REQUEST_DATE_UTC',
	            width: 22,
	            value: (r) => this.datePipe.transform(r.createdDateTime, 'dd/MM/yyyy HH:mm:ss', 'UTC')
	        },
            	        {
	            key: 'statusi18n',
	            i18n: 'MY_REQUESTS.STATUS',
	            width: 14,
	            value: (r) => this.translate.instant(`STATUS.${r.status}`)
	        },
	        {
	            key: 'substatusi18n',
	            i18n: 'MY_REQUESTS.SUBSTATUS',
	            width: 34,
	            value: (r) => this.translate.instant(`SUBSTATUS.${r.substatus}`)
	        },
	        // {
	        //     key: 'status',
	        //     i18n: 'MY_REQUESTS.RAW_STATUS',
	        //     width: 20
	        // },
	        // {
	        //     key: 'substatus',
	        //     i18n: 'MY_REQUESTS.RAW_SUBSTATUS',
	        //     width: 28
	        // },
	    ];
    }
    // #endregion
}
